import React, { ReactElement, useContext, useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import TablePagination from "@material-ui/core/TablePagination";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import CustomFooterGraphic from "../../Requests/CustomFooterGraphic";
import CustomProgress from "../../../components/Alert/CustomProgress";
import ProductApi from "../../../api/services/ProductApi";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { getRowsPerPage, setRowsPerPage } from "../../../helper/UserSettingsHelper";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import useEnv from "../../../hooks/env";
import { fadeIn } from "../../../styles/springs";

interface UnknownProductOverviewTableProps {
  unknownProductPagedRes: ProductRaw.Schemas.UnknownProductPagedResult | undefined;
  handleEdit: (row: CustomTableRowData) => void;
  setUnknownProductPagedRes: (res: ProductRaw.Schemas.UnknownProductPagedResult | undefined) => void;
}

const UnknownProductOverviewTable = (props: UnknownProductOverviewTableProps): ReactElement => {
  const { unknownProductPagedRes, handleEdit, setUnknownProductPagedRes } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(getRowsPerPage());
  const springProps = useSpring(fadeIn);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const rowsPerPage = parseInt(event.target.value, 10);
    setPageSize(rowsPerPage);
    setRowsPerPage(rowsPerPage);
  };

  const generateRows = (productArr: ProductRaw.Schemas.UnknownProductResponse[] | undefined): void => {
    const productRows =
      productArr?.map((product) => {
        return {
          id: product.id,
          productName: product.name,
          description: product.description,
          type: formatIdentifierAsString(product.type || ""),
          creationDate: product.creationDate,
        };
      }) || [];

    setRows(productRows as never);
  };

  useEffect(() => {
    generateRows(unknownProductPagedRes?.data as ProductRaw.Schemas.UnknownProductResponse[]);
    setIsLoading(false);
  }, [unknownProductPagedRes]);

  useEffect(() => {
    setIsLoading(true);
    productApi
      .getUnknownProducts("", pageIndex, pageSize)
      .then((res) => {
        setUnknownProductPagedRes(res.data);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        handleHttpError(e);
      });
  }, [pageSize, pageIndex]);

  return isLoading ? (
    <CustomProgress />
  ) : (
    <animated.div style={springProps}>
      <CustomTable
        boldIndex={1}
        columns={["ID", "Product Name", "Description", "Type", "Creation Date"]}
        columnMaxWidths={["4rem", "15vw", "10vw", "10vw", "4rem"]}
        actionColumnIndexes={[1]}
        formatDates
        rows={rows}
        handleEdit={handleEdit}
      />
      <TablePagination
        component="div"
        count={unknownProductPagedRes?.totalData || 0}
        page={pageIndex}
        onChangePage={handleChangePage}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
      <CustomFooterGraphic />
    </animated.div>
  );
};

export default UnknownProductOverviewTable;
